/**
 * Ticket 124.6c — Parent subtree roll-up.
 *
 * Typed client + pure helpers for `GET /api/v1/me/subtree-rollup`
 * (gated TENANT_ADMIN). The endpoint aggregates the caller tenant's
 * descendant subtree: a `subtreeTotal` across the whole subtree plus a
 * `perChild` row per subtree tenant. A leaf tenant gets a single
 * `perChild` row (itself); a parent gets one row per subtree tenant.
 *
 * Shapes mirror the backend JSON exactly (camelCase).
 */

import { apiClient } from '@/lib/apiClient';

export interface SubtreeMetrics {
  leadsCount: number;
  opportunitiesCount: number;
  pipelineValue: number;
  avgSnapshotScore: number;
}

export interface SubtreeChildRow extends SubtreeMetrics {
  tenantId: string;
  name: string;
}

export interface SubtreeRollup {
  subtreeTotal: SubtreeMetrics;
  perChild: SubtreeChildRow[];
}

/** Fetch the caller tenant's subtree roll-up. */
export async function fetchSubtreeRollup(): Promise<SubtreeRollup> {
  const { data } = await apiClient.get<SubtreeRollup>('/me/subtree-rollup');
  return data;
}

/**
 * A roll-up represents a true parent only when its subtree spans more
 * than the caller tenant itself (a leaf returns `perChild=[self]`). This
 * is the fallback parent-detection signal when the tenant-hierarchy node
 * is unavailable.
 */
export function isParentRollup(rollup: SubtreeRollup | null | undefined): boolean {
  return (rollup?.perChild.length ?? 0) > 1;
}

/** Format a count metric (leads / opportunities) for display. */
export function formatSubtreeCount(value: number): string {
  return Math.round(value).toLocaleString();
}

/** Format the pipeline value as currency (USD, no fractional cents). */
export function formatSubtreePipeline(value: number): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `$${Math.round(value)}`;
  }
}

/** Format the average snapshot score (0–100 scale, whole number). */
export function formatSubtreeScore(value: number): string {
  return String(Math.round(value));
}
